import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Camera } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a photographer invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A photographer invoice should include your name or studio name and contact details, the client name and address, a unique invoice number and shoot date, a description of services (session type, hours, images delivered), session fee, editing fee, travel expenses, any licensing or usage rights charges, the total amount due, and your payment terms and bank details.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do photographers invoice for packages?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For photography packages, list the package name as a single line item with the agreed price. Add separate line items for extras — additional editing hours, travel, print orders, or licensing fees. If a deposit was paid, show it as a deduction from the subtotal so the client can clearly see the balance remaining.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should photographers charge tax on invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'It depends on your country and registration status. In the UK, add 20% VAT only if VAT-registered. In India, add 18% GST on photography services if your annual turnover exceeds ₹20 lakh. In Australia, add 10% GST if turnover exceeds AUD $75,000. US photographers may need to charge sales tax on physical products like prints depending on state rules, but most service fees are exempt.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I invoice for a photography usage licence?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Add the usage licence as a separate line item. Describe it clearly — for example: "Commercial usage licence — nationwide digital campaign, 12 months." Specify the licence duration, territory, and media type. Keeping the licence fee separate from the shoot fee helps both parties understand what rights are being purchased.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this photographer invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free photographer invoice template. Fill in your details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function PhotographerInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Photographer Invoice Template: Free PDF for Photographers | Invilic"
        description="Free photographer invoice template. Bill for photo sessions, editing, travel, and usage licences. Download a professional PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/photographer-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Photographer Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template built for photographers. Bill for sessions, editing, travel, and usage licences. Fill in online and download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-violet-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Photographer
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Wedding & portrait photographers', desc: 'Bill clients for full-day coverage, editing packages, albums, and print deliverables.' },
              { title: 'Commercial photographers', desc: 'Invoice for product shoots, advertising campaigns, and commercial usage licences.' },
              { title: 'Event photographers', desc: 'Bill for corporate events, conferences, parties, and sports coverage by the hour or day.' },
              { title: 'Real estate photographers', desc: 'Issue invoices for property shoots, drone footage, virtual tours, and editing.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center flex-shrink-0">
                  <Camera className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Photographer Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Photographer / studio name and logo',
              'Client name, address and contact',
              'Invoice number and shoot date',
              'Session type and description',
              'Session fee (hourly or package rate)',
              'Editing and post-processing fee',
              'Travel and accommodation expenses',
              'Usage licence fee (if applicable)',
              'Deposit paid — balance remaining',
              'Subtotal, tax (VAT/GST) and discount',
              'Total amount due',
              'Payment terms and bank details',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-violet-100 dark:border-violet-800/30 bg-violet-50 dark:bg-violet-900/10">
                <CheckCircle className="w-4 h-4 text-violet-600 dark:text-violet-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Photography Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Fill in your work', desc: 'Add your details, shoot info, expenses, and any licence fees. Takes under 2 minutes.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to email directly to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-violet-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Photographer Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Photography Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-violet-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Photographer Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
